// LernoDownload.js — Public download page for LERNO CO print-kit orders.
// No login. No account. The URL is /download/:token and the token is the
// entire receipt. Deliberately minimal / print-materials-shop aesthetic
// so nothing here reads like a SaaS dashboard.
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function bytes(n) {
  if (!n) return "-";
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function LernoDownload() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/api/lerno/download/${token}/meta`)
      .then((r) => setMeta(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Download link not found."))
      .finally(() => setLoading(false));
  }, [token]);

  const startDownload = () => {
    window.location.href = `${API}/api/lerno/download/${token}`;
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F8FAFC",
        fontFamily:
          "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: "#0F172A",
        padding: "48px 20px",
      }}
    >
      <div
        style={{
          maxWidth: 640,
          margin: "0 auto",
          background: "#FFF",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "24px 32px",
            borderBottom: "2px solid #1E293B",
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1.5,
              textTransform: "uppercase",
              color: "#64748B",
              marginBottom: 4,
            }}
            data-testid="lerno-download-brand"
          >
            LERNO CO · MOSAIC Print Materials
          </div>
          <h1 style={{ fontSize: 22, margin: 0, letterSpacing: -0.3 }}>
            Your MOSAIC print kit
          </h1>
        </div>

        {loading && (
          <div style={{ padding: "40px 32px", color: "#64748B" }} data-testid="lerno-download-loading">
            Loading your order…
          </div>
        )}

        {!loading && error && (
          <div style={{ padding: "40px 32px" }} data-testid="lerno-download-error">
            <div
              style={{
                background: "#FEF2F2",
                borderLeft: "4px solid #DC2626",
                padding: "16px 20px",
                borderRadius: 6,
                color: "#7F1D1D",
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                We couldn't open this download link.
              </div>
              <div style={{ fontSize: 14 }}>{error}</div>
              <div style={{ marginTop: 16, fontSize: 13, color: "#334155" }}>
                If your license has expired or you believe this is a mistake,
                please reply to the email that included this link or contact
                LERNO CO with your order ID.
              </div>
            </div>
          </div>
        )}

        {!loading && meta && (
          <div style={{ padding: "32px" }}>
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  color: "#64748B",
                }}
              >
                Order
              </div>
              <div style={{ fontSize: 15, fontWeight: 500 }} data-testid="lerno-order-id">
                {meta.order_id}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.8, color: "#64748B" }}>
                School
              </div>
              <div style={{ fontSize: 15 }} data-testid="lerno-school-name">
                {meta.school_name}
              </div>
              <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>
                PO&nbsp;# {meta.po_number}
              </div>
            </div>

            <div
              style={{
                background: "#F1F5F9",
                borderRadius: 8,
                padding: 20,
                marginBottom: 28,
              }}
            >
              <table style={{ width: "100%", fontSize: 14 }}>
                <tbody>
                  <tr>
                    <td style={{ color: "#475569", padding: "4px 0", width: 160 }}>
                      Grades included
                    </td>
                    <td style={{ padding: "4px 0" }}>
                      {meta.grades_included.map((g) => `Grade ${g}`).join(", ")}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#475569", padding: "4px 0" }}>Student copies</td>
                    <td style={{ padding: "4px 0" }}>{meta.total_student_copies} total</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#475569", padding: "4px 0" }}>Academic year</td>
                    <td style={{ padding: "4px 0" }}>{meta.academic_year_label}</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#475569", padding: "4px 0" }}>Valid through</td>
                    <td style={{ padding: "4px 0", fontWeight: 600 }}>
                      {fmtDate(meta.license_expires_at)}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#475569", padding: "4px 0" }}>Package size</td>
                    <td style={{ padding: "4px 0" }}>{bytes(meta.zip_bytes)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {meta.can_download ? (
              <button
                data-testid="lerno-download-btn"
                onClick={startDownload}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "16px 24px",
                  background: "#1E293B",
                  color: "#FFF",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 15,
                  fontWeight: 600,
                  letterSpacing: 0.2,
                  cursor: "pointer",
                }}
              >
                Download your print kit ({bytes(meta.zip_bytes)})
              </button>
            ) : (
              <div
                data-testid="lerno-download-expired"
                style={{
                  background: "#FEF2F2",
                  borderLeft: "4px solid #DC2626",
                  padding: "16px 20px",
                  borderRadius: 6,
                  color: "#7F1D1D",
                }}
              >
                <div style={{ fontWeight: 600 }}>License expired.</div>
                <div style={{ fontSize: 14, marginTop: 4 }}>
                  Your print license ended on {fmtDate(meta.license_expires_at)}.
                  Contact LERNO CO to renew or extend.
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: 32,
                paddingTop: 20,
                borderTop: "1px solid #E2E8F0",
                fontSize: 12,
                color: "#64748B",
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "#334155" }}>License:</strong>{" "}
              Single-school print use through {fmtDate(meta.license_expires_at)}.
              Print unlimited paper copies for classroom use at{" "}
              {meta.school_name}. No digital redistribution. Full terms in the
              LICENSE.txt file inside the download.
            </div>
            <div
              style={{
                marginTop: 16,
                fontSize: 12,
                color: "#94A3B8",
              }}
            >
              Save this page. Your print license is valid through{" "}
              <strong style={{ color: "#334155" }}>
                {fmtDate(meta.license_expires_at)}
              </strong>{" "}
              — you can re-download as often as you need until then, from
              any browser. If you lose the email, use the recovery form at{" "}
              <a href="/download-lookup" style={{ color: "#1D4ED8" }}>
                /download-lookup
              </a>{" "}
              (school PO # + coordinator email).
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          maxWidth: 640,
          margin: "16px auto 0",
          fontSize: 11,
          color: "#94A3B8",
          textAlign: "center",
        }}
      >
        © LERNO CO · Printed Instructional Materials
      </div>
    </div>
  );
}
